import React, { useState, useEffect } from "react";
import { Download, X } from "lucide-react";
import { useLang } from "@/i18n";
import { LOGO } from "@/assets";

const DISMISS_KEY = "ab_pwa_dismissed";
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };
const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };

const PWAInstallBanner = () => {
  const { t } = useLang();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (safeGet(DISMISS_KEY)) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    const onInstalled = () => { setShow(false); safeSet(DISMISS_KEY, "1"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setShow(false);
    setDeferred(null);
    safeSet(DISMISS_KEY, "1");
  };

  const dismiss = () => { setShow(false); safeSet(DISMISS_KEY, "1"); };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 inset-x-4 z-[60] md:hidden animate-[fadeInUp_0.4s_ease-out]"
    >
      <div className="ab-hairline bg-ab-black/95 backdrop-blur-xl rounded-2xl shadow-2xl p-4 flex items-center gap-3">
        <img src={LOGO} alt="" className="w-11 h-11 rounded-full ring-1 ring-ab-gold/40 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-serif text-ab-ivory text-base leading-tight">{t("pwa_title")}</p>
          <p className="text-ab-ivory/60 text-xs mt-0.5 leading-snug">{t("pwa_text")}</p>
        </div>
        <button
          onClick={install}
          data-testid="pwa-install-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-ab-gold text-ab-black px-4 py-2.5 text-xs font-semibold tracking-wide hover:bg-ab-gold-2 transition-colors"
        >
          <Download size={15} /> {t("pwa_install")}
        </button>
        <button
          onClick={dismiss}
          data-testid="pwa-dismiss-btn"
          aria-label={t("pwa_dismiss")}
          className="shrink-0 text-ab-ivory/50 hover:text-ab-ivory p-1"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
